'use client';

import { useEffect, useRef, useState } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useCookies } from 'react-cookie';
import { ROUTES } from 'helpers/constants/routes';
import usePath from 'helpers/hooks/usePath';
import { buildPageviewFlowType } from 'helpers/utils/buildPageviewFlowType';

export const AnalyticsParameters = {
  IOS: 'ios',
  Android: 'android',
  Hybrid: 'hybrid',
} as const;

export default function useTrackPageRoute() {
  const pathname = usePath(); // Should return path like "/find-store"
  const [cookies] = useCookies(['site-variant']);
  const flags = useFlags();
  const ldClient = useLDClient();
  const [isLDReady, setIsLDReady] = useState(false);
  const lastTrackedPath = useRef<string | null>(null);

  // Wait for LaunchDarkly to finish initializing before tracking the first
  // pageview, so flowType carries flag context. If LD fails to init (e.g.
  // blocked by ad-blocker), still mark ready so analytics is not lost.
  useEffect(() => {
    if (!ldClient) return;
    let cancelled = false;
    ldClient
      .waitUntilReady()
      .catch(() => {
        /* swallow — fall through to mark ready anyway */
      })
      .finally(() => {
        if (!cancelled) setIsLDReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [ldClient]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isLDReady) return;
    if (lastTrackedPath.current === pathname.absolutePath) return;
    lastTrackedPath.current = pathname.absolutePath;

    const fullUrl = window.location.href;

    // The retailer-handoff page owns its own enriched virtualPageview
    // (useHandoffPageview) — skip the generic push there so each handoff
    // load produces exactly one pageview.
    const isHandoffRoute = pathname.pathWithoutQuery === ROUTES.RETAILER_HANDOFF_ROUTE;

    if (!isHandoffRoute) {
      // Send virtualPageview event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'virtualPageview',
        flowType: buildPageviewFlowType(flags),
        flowName: cookies['site-variant'], //this filed can be used if we are planning to have both imperva and launch darkley flags
        page: pathname,
        location: fullUrl,
      });
    }

    // Google Analytics (optional)
    if (typeof window.gtag === 'function') {
      window.gtag('config', process.env.NEXT_PUBLIC_GTAG_ID || '', {
        page_path: pathname,
      });
    }
  }, [cookies, pathname, flags, isLDReady]);

  return null;
}
